import { useState, useEffect, useRef, useCallback } from "react";
import * as IMG from "./images.js";
import "./App.css";

// ─── DATA ────────────────────────────────────────────────────────────────────

const PROJECTS = [
  {
    id: "airline",
    title: "Airline Route Profit Maximization",
    timeline: "Nov. – Dec. 2025",
    role: "Full-Stack Engineer",
    desc: "Simulated airline management system in Java for Logan Airport. Built staff UIs for flight scheduling, maintenance tracking, and aircraft management. Implemented route profitability analytics — achieving 30% improvement in route revenue after optimization.",
    tags: ["Java", "OOP", "UI Design", "Data Modeling", "GitHub"],
    images: [
      { src: IMG.airline1, caption: "Airline Route – Flight Management UI" },
      { src: IMG.airline2, caption: "Airline Route – Analytics Dashboard" },
    ],
  },
  {
    id: "studyhub",
    title: "StudyHub – Learning Resource Platform",
    timeline: "Nov. – Dec. 2025",
    role: "Front-End Engineer",
    desc: "Full-stack web platform for student collaboration featuring a messageboard with posts, likes, and replies. Includes JWT authentication, bcrypt password hashing, and DeepSeek AI integration for resource recommendations.",
    tags: ["React", "Node.js", "MongoDB", "Redux", "JWT", "DeepSeek API"],
    images: [],
  },
  {
    id: "anemone",
    title: "Project Anemone – Satellite Data Viz",
    timeline: "Dec. 2022 – Jun. 2023",
    role: "Front-End Engineer · Capstone w/ Starfish Space",
    desc: "Data visualization platform for the startup Starfish Space. Engineered an interactive layer-comparison view for side-by-side telemetry graph analysis across multiple test runs, with PNG export and searchable legend.",
    tags: ["React.js", "HTML", "CSS", "JavaScript", "Figma"],
    images: [
      { src: IMG.anemone_logo, caption: "Project Anemone – Logo" },
      { src: IMG.anemone_layer, caption: "Project Anemone – Layer Comparison View" },
    ],
  },
  {
    id: "oceans",
    title: "OceansFive – Snorkeling Finder",
    timeline: "Apr. – Jun. 2023",
    role: "Front-End Engineer",
    desc: "React web app helping users discover snorkeling locations in Oahu, Hawaii. Designed interactive location cards with amenity icons, weather data, wildlife reports, and per-location safety checklists.",
    tags: ["React.js", "JavaScript", "HTML", "CSS", "Figma"],
    images: [
      { src: IMG.oceans_hero, caption: "OceansFive – Search Hero" },
      { src: IMG.oceans_cards, caption: "OceansFive – Location Cards" },
    ],
  },
  {
    id: "rcubed",
    title: "RCubed – Electronics Recycling App",
    timeline: "Jan. – Mar. 2022",
    role: "UX Designer",
    desc: "Mobile app prototype helping University of Washington students locate nearby electronics recycling, repair, and reuse drop-off points via an interactive map with live tooltips showing hours and distance.",
    tags: ["Figma", "UX Design", "Mobile", "Prototyping"],
    images: [
      { src: IMG.rcubed_logo, caption: "RCubed – Logo", contain: true },
      { src: IMG.rcubed_map1, caption: "RCubed – Campus Map" },
    ],
  },
  {
    id: "greenroof",
    title: "GreenRoof – Solar Panel Finder",
    timeline: "Jul. – Aug. 2020",
    role: "Project Manager · Designer",
    desc: "Figma prototype helping homeowners evaluate solar installation costs, track installation progress, and find nearby solar providers via an integrated map with ratings and pricing comparison.",
    tags: ["Figma", "UX Design", "Mobile", "Project Management"],
    images: [
      { src: IMG.greenroof_logo, caption: "GreenRoof – Logo", contain: true },
      { src: IMG.greenroof_dash, caption: "GreenRoof – Savings Dashboard" },
    ],
  },
];

const SKILLS = {
  Languages: ["Python", "Java", "JavaScript", "R", "Bash", "HTML", "CSS", "SQL", "YAML"],
  "Tools & Frameworks": ["React", "Angular", "Node.js", "Redux", "MongoDB", "Git", "GitHub Actions", "Pandas", "NumPy", "Figma", "Balsamiq", "jQuery", "bcrypt", "JWT"],
  "Core Skills": ["Prompt Engineering", "Automation", "Data Science", "Web Development", "Agile / Scrum", "UI / UX Design"],
};

const EXPERIENCE = [
  { role: "Software Engineer Intern", place: "GigaTECH LLC", date: "Jul. 2024 – Oct. 2024 · Arlington, VA" },
  { role: "Data Engineer Intern", place: "Semantic Web Company", date: "Jun. 2023 – Jul. 2023" },
  { role: "Software Engineering Capstone", place: "In collaboration with Starfish Space", date: "Dec. 2022 – Jun. 2023" },
];

const EDUCATION = [
  { role: "M.S. Information Systems · GPA 3.8", place: "Northeastern University", date: "Expected May 2027 · Boston, MA" },
  { role: "B.S. Informatics · Software Development", place: "University of Washington", date: "Jul. 2023 · Seattle, WA" },
];

// ─── HOOKS ───────────────────────────────────────────────────────────────────

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ─── LIGHTBOX ────────────────────────────────────────────────────────────────

function Lightbox({ images, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const step = useCallback((dir) => setIdx(i => (i + dir + images.length) % images.length), [images.length]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", handler); document.body.style.overflow = ""; };
  }, [onClose, step]);

  return (
    <div className="lb-overlay" onClick={onClose}>
      <button className="lb-close" onClick={onClose}>✕</button>
      <div className="lb-img-wrap" onClick={e => e.stopPropagation()}>
        <img src={images[idx].src} alt={images[idx].caption} />
      </div>
      <div className="lb-nav" onClick={e => e.stopPropagation()}>
        <button className="lb-btn" onClick={() => step(-1)}>← Prev</button>
        <span className="lb-counter">{idx + 1} / {images.length}</span>
        <button className="lb-btn" onClick={() => step(1)}>Next →</button>
      </div>
      <div className="lb-caption">{images[idx].caption}</div>
    </div>
  );
}

// ─── PROJECT CARD ────────────────────────────────────────────────────────────

function ProjectCard({ project, onImageClick, delay }) {
  const [ref, visible] = useInView(0.08);
  return (
    <div
      ref={ref}
      className={`project-card ${visible ? "visible" : ""}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {project.images.length > 0 ? (
        <div className="card-imgs">
          {project.images.map((img, i) => (
            <img
              key={i}
              src={img.src}
              alt={img.caption}
              className={img.contain ? "contain" : ""}
              onClick={() => onImageClick(project.images, i)}
            />
          ))}
        </div>
      ) : (
        <div className="card-placeholder">[ SCREENSHOTS COMING SOON ]</div>
      )}
      <div className="card-header">
        <span className="card-timeline">{project.timeline}</span>
        <span className="card-role-badge">{project.role.split("·")[0].trim()}</span>
      </div>
      <div className="card-body">
        <div className="card-title">{project.title}</div>
        <p className="card-desc">{project.desc}</p>
        <div className="card-tags">
          {project.tags.map(t => <span key={t} className="tag">{t}</span>)}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────────────────────

export default function App() {
  const [lightbox, setLightbox] = useState(null); // { images, startIndex }
  const [activeSection, setActiveSection] = useState("home");

  // Active nav tracking
  useEffect(() => {
    const handler = () => {
      const sections = ["home", "projects", "skills", "about", "contact"];
      for (const id of [...sections].reverse()) {
        const el = document.getElementById(id);
        if (el && window.scrollY >= el.offsetTop - 100) { setActiveSection(id); break; }
      }
    };
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <div className="app">
      {/* NAV */}
      <nav>
        <div className="nav-logo">OS <span>// Portfolio</span></div>
        <div className="nav-links">
          {["home","projects","skills","about"].map(s => (
            <a key={s} href={`#${s}`} className={activeSection === s ? "active" : ""}>{s.charAt(0).toUpperCase() + s.slice(1)}</a>
          ))}
        </div>
        <a href="#contact" className="nav-cta">Contact Me</a>
      </nav>

      {/* HERO */}
      <section id="home" className="hero-section">
        <div className="hero-inner">
          <div className="hero-left">
            <div className="hero-tag fade-in-1">Software Engineer</div>
            <h1 className="fade-in-2">Hello, I'm <em>Owen</em>—<br/>full-stack engineer<br/></h1>
            <p className="hero-desc fade-in-3">M.S. Information Systems @ Northeastern. Building at the intersection of full-stack development, workflow automation, and data systems. Driven by systems that are intelligent by design — not just functional.</p>
            <div className="hero-btns fade-in-4">
              <a href="#projects" className="btn-primary">View Projects ↓</a>
              <a href="mailto:schaff.o@northeastern.edu" className="btn-secondary">Get In Touch</a>
            </div>
          </div>
          <div className="hero-right">
            {[
              { quote: "Owen quickly adapted to our Agile environment and delivered high-quality GitHub Actions workflows that solved real pain points for our engineering team.", src: "Software Engineer · GigaTECH LLC" },
              { quote: "Owen's knowledge graph work on sustainable travel showed genuine curiosity and technical rigor.", src: "Data Engineer · Semantic Web Company" },
            ].map((t, i) => (
              <div key={i} className="testimonial">
                <p>{t.quote}</p>
                <div className="testimonial-src">{t.src}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="projects-section">
        <div className="section-header">
          <span className="section-label">Work</span>
          <h2>Projects</h2>
        </div>
        <div className="projects-grid">
          {PROJECTS.map((p, i) => (
            <ProjectCard
              key={p.id}
              project={p}
              delay={(i % 3) * 100}
              onImageClick={(images, startIndex) => setLightbox({ images, startIndex })}
            />
          ))}
        </div>
      </section>

      {/* SKILLS */}
      <section id="skills" className="skills-section">
        <div className="skills-inner">
          <div className="section-header">
            <span className="section-label">Competencies</span>
            <h2>Skills</h2>
          </div>
          <div className="skills-grid">
            <div className="skills-box">
              {Object.entries(SKILLS).map(([cat, pills]) => (
                <div key={cat}>
                  <h3>{cat}</h3>
                  <div className="skill-pills">
                    {pills.map(p => <span key={p} className="skill-pill">{p}</span>)}
                  </div>
                </div>
              ))}
            </div>
            <div className="skills-box">
              <h3>Industry Experience</h3>
              <div className="exp-list">
                {EXPERIENCE.map((e, i) => (
                  <div key={i} className="exp-item">
                    <div className="exp-role">{e.role}</div>
                    <div className="exp-place">{e.place}</div>
                    <div className="exp-date">{e.date}</div>
                  </div>
                ))}
              </div>
              <h3>Education</h3>
              <div className="exp-list">
                {EDUCATION.map((e, i) => (
                  <div key={i} className="exp-item">
                    <div className="exp-role">{e.role}</div>
                    <div className="exp-place">{e.place}</div>
                    <div className="exp-date">{e.date}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="about-section">
        <div className="about-inner">
          <div className="about-text">
            <span className="section-label">About Me</span>
            <h2>Building things<br/>that think.</h2>
            <p>I'm a software engineer and <strong>M.S. Information Systems</strong> student at Northeastern University, building at the intersection of full-stack development, workflow automation, and data systems.</p>
            <p>With a <strong>B.S. in Informatics</strong> from the University of Washington, I've spent my career turning complex technical challenges into clean, maintainable solutions — from automating developer workflows at GigaTECH to constructing semantic knowledge graphs at the Semantic Web Company.</p>
            <p>I bring both the <strong>technical depth</strong> and the communication skills to thrive across teams. I'm driven by systems that are intelligent by design — not just functional.</p>
            <a href="mailto:schaff.o@northeastern.edu" className="btn-primary" style={{marginTop:"2rem",display:"inline-block"}}>schaff.o@northeastern.edu</a>
          </div>
          <div className="about-photo-wrap">
            <div className="about-photo-placeholder">OS</div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="contact-section">
        <div className="contact-inner">
          <div className="contact-left">
            <h2>Get In Touch</h2>
            <p>Open to new opportunities, collaborations, and conversations.</p>
          </div>
          <div className="contact-links">
            <a href="mailto:schaff.o@northeastern.edu" className="contact-link">✉ Email</a>
            <a href="https://www.linkedin.com/in/owen-schaff/" className="contact-link" target="_blank" rel="noreferrer">in LinkedIn</a>
            <a href="https://github.com/OwenSchaffNortheastern" className="contact-link" target="_blank" rel="noreferrer">⌥ GitHub</a>
          </div>
        </div>
      </section>

      <footer>
        <span>OS // Portfolio · Brookline, MA</span>
        <span>Built with React</span>
      </footer>

      {/* LIGHTBOX */}
      {lightbox && (
        <Lightbox
          images={lightbox.images}
          startIndex={lightbox.startIndex}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
